import React from "react";
import AddIcon from "@mui/icons-material/Add";

import { DatabaseIndexDto } from "../../lib/generated/api-client";
import { Field, humanize } from "../../lib/schema-field";
import { useDataManagement } from "../../lib/useDataManagement";
import { IndexList } from "../IndexList";
import { DashboardCard } from "./DashboardCard";
import { Button } from "../ui/Button";

interface TableCardProps {
  datasourceId: string;
  name: string;
  collection: string;
  schema: Field[];
  indexes?: DatabaseIndexDto[];
  primaryFieldName?: string;
  demoMode?: boolean;
  readOnly?: boolean;
  /**
   * `card` (default) is the Overview leftover: a named 420px pane inside
   * DashboardCard, with the inner Manager heading skipped so the card names
   * the table once.
   * `page` is the post-8674e9e shape: one entity owns the route, so render
   * the same full-height Manager Collection.tsx uses (heading + count + grid).
   */
  variant?: "card" | "page";
}

/**
 * One component per data table, because useDataManagement binds to a single
 * collection. The old page called the hook once and rendered its element inside
 * a map, so every table on a dashboard showed the first table's rows.
 */
export const TableCard: React.FC<TableCardProps> = ({
  datasourceId,
  collection,
  schema,
  indexes,
  primaryFieldName,
  demoMode,
  readOnly,
  variant = "card",
}) => {
  const embedded = variant === "card";
  const { Manager, openForm, total, loading } = useDataManagement({
    datasourceId,
    collectionName: collection,
    schema,
    demoMode,
    readOnly,
    embedded,
  });

  if (variant === "page") {
    // Collection.tsx: the Manager header is the single heading. Do not wrap
    // it in DashboardCard or a 420px clip — that was Overview chrome.
    return (
      <div className="flex h-full min-h-0 flex-col gap-2.5">
        {Manager}
        <IndexList indexes={indexes} primaryFieldName={primaryFieldName} />
      </div>
    );
  }

  // One name: the card heading. Count lives here too so the inner Manager
  // does not reprint `task_dependencies` / Task Dependencies / Task Dependencies.
  const countLabel =
    loading && !total
      ? "Loading…"
      : `${total.toLocaleString()} ${total === 1 ? "record" : "records"}`;

  // New record lives on the card header, not on a reserved empty band above
  // the toolbar. The embedded Manager renders no header of its own.
  const action = readOnly ? undefined : (
    <Button
      onClick={() => openForm()}
      variant="secondary" size="sm" className="od-new-record border-od-primary font-semibold text-od-primary-text hover:bg-transparent hover:opacity-90"
    >
      <AddIcon sx={{ fontSize: 16 }} /> New record
    </Button>
  );

  return (
    <DashboardCard
      title={humanize(collection)}
      description={countLabel}
      action={action}
      bodyClassName="p-0"
    >
      {/* Content-sized up to a cap, not pinned. A 2-row table used to paint
          ~120px of empty surface under its footer because every card body was
          h-[420px] regardless of row count; a 50-row table still stops at the
          cap and scrolls inside its own pane. */}
      <div className="flex max-h-[420px] min-h-0 min-w-0 flex-col">{Manager}</div>
    </DashboardCard>
  );
};

export default TableCard;
